/* =====================================================================
   DIGITAL TWIN — SAMPLE DATA (illustrative, not from a real factory)

   This file is the ONLY source of numbers for the digital twin
   demonstration. The canvas, station list, metrics and before/after
   comparison are all calculated from it in  src/twin/calc.js , so
   they always agree with each other.

   Shape = the proposed API contract (docs/API_CONTRACTS.md):
     factory → lines → stations   (+ shift, targets, scenarios)

   Units
     smv            standard minutes per piece for ONE operator (min/pc)
     operators      people assigned to the operation (count)
     performance    observed pace vs. standard, 0–1 (0.85 = 85 %)
     downtimeMin    machine stopped time so far this shift (minutes)
     state          "running" | "stopped"  — "idle" is derived by calc.js
                    when a station is starved by the bottleneck
   To edit the demo: change numbers here. Nothing else needs to change.
   ===================================================================== */

export const SAMPLE_SNAPSHOT = {
  schemaVersion: 1,
  source: "sample", // "sample" | "api" — shown to visitors as "Sample data"
  generatedAt: "2026-10-24T12:00:00+09:00",
  factory: { id: "F-DEMO", name: { en: "Sample factory", ko: "샘플 공장" }, timezone: "Asia/Seoul" },

  // One shift: 08:00–17:00 with a one-hour lunch = 8 working hours.
  // The snapshot is taken at 12:00, so 4 working hours have passed.
  shift: { start: "08:00", end: "17:00", breakStartMin: 240, breakMin: 60, workingMin: 480, elapsedMin: 240, now: "12:00" },

  lines: [
    {
      id: "L3",
      name: { en: "Line 3", ko: "3라인" },
      style: { en: "KS-2419 men's Oxford shirt", ko: "KS-2419 남성 옥스퍼드 셔츠" },
      dailyTarget: 600,
      bundleSize: 24, // pieces per bundle
      wipLimit: 48,   // most pieces allowed to wait between two stations (2 bundles)
      stations: [
        { id: "L3-01", op: { en: "Collar run-stitch", ko: "칼라 박음질" }, smv: 0.9, operators: 3, performance: 0.85, state: "running", downtimeMin: 0 },
        { id: "L3-02", op: { en: "Collar turn & top-stitch", ko: "칼라 뒤집기·상침" }, smv: 1.1, operators: 2, performance: 0.82, state: "running", downtimeMin: 4 },
        { id: "L3-03", op: { en: "Cuff make", ko: "커프스 제작" }, smv: 0.8, operators: 2, performance: 0.88, state: "running", downtimeMin: 0 },
        { id: "L3-04", op: { en: "Front placket", ko: "앞단 플라켓" }, smv: 0.7, operators: 3, performance: 0.86, state: "running", downtimeMin: 0 },
        { id: "L3-05", op: { en: "Pocket attach", ko: "포켓 부착" }, smv: 0.6, operators: 1, performance: 0.84, state: "stopped", downtimeMin: 18 },
        { id: "L3-06", op: { en: "Shoulder join", ko: "어깨 연결" }, smv: 0.5, operators: 1, performance: 0.9, state: "running", downtimeMin: 0 },
        { id: "L3-07", op: { en: "Sleeve attach", ko: "소매 부착" }, smv: 1.6, operators: 2, performance: 0.8, state: "running", downtimeMin: 0 },
        { id: "L3-08", op: { en: "Side seam", ko: "옆선 봉제" }, smv: 1.2, operators: 2, performance: 0.83, state: "running", downtimeMin: 0 },
        { id: "L3-09", op: { en: "Collar attach", ko: "칼라 부착" }, smv: 1.3, operators: 2, performance: 0.84, state: "running", downtimeMin: 6 },
        { id: "L3-10", op: { en: "Hem", ko: "밑단" }, smv: 0.6, operators: 1, performance: 0.87, state: "running", downtimeMin: 0 },
      ],
    },
    {
      id: "L1",
      name: { en: "Line 1", ko: "1라인" },
      style: { en: "KS-2512 knit polo", ko: "KS-2512 니트 폴로" },
      dailyTarget: 720,
      bundleSize: 24,
      wipLimit: 48,
      stations: [
        { id: "L1-01", op: { en: "Placket set", ko: "플라켓 부착" }, smv: 1.0, operators: 2, performance: 0.86, state: "running", downtimeMin: 0 },
        { id: "L1-02", op: { en: "Shoulder join", ko: "어깨 연결" }, smv: 0.45, operators: 1, performance: 0.88, state: "running", downtimeMin: 0 },
        { id: "L1-03", op: { en: "Collar attach", ko: "칼라 부착" }, smv: 1.2, operators: 2, performance: 0.83, state: "running", downtimeMin: 9 },
        { id: "L1-04", op: { en: "Sleeve attach", ko: "소매 부착" }, smv: 1.1, operators: 2, performance: 0.84, state: "running", downtimeMin: 0 },
        { id: "L1-05", op: { en: "Side seam", ko: "옆선 봉제" }, smv: 0.9, operators: 3, performance: 0.85, state: "running", downtimeMin: 0 },
        { id: "L1-06", op: { en: "Sleeve hem", ko: "소매 밑단" }, smv: 0.7, operators: 1, performance: 0.86, state: "stopped", downtimeMin: 24 },
        { id: "L1-07", op: { en: "Bottom hem", ko: "밑단" }, smv: 0.6, operators: 1, performance: 0.88, state: "running", downtimeMin: 0 },
        { id: "L1-08", op: { en: "Label & tack", ko: "라벨·택" }, smv: 0.4, operators: 1, performance: 0.9, state: "running", downtimeMin: 0 },
      ],
    },
    {
      id: "L5",
      name: { en: "Line 5", ko: "5라인" },
      style: { en: "KS-2506 chino trouser", ko: "KS-2506 치노 팬츠" },
      dailyTarget: 420,
      bundleSize: 12,
      wipLimit: 36,
      stations: [
        { id: "L5-01", op: { en: "Back pocket", ko: "뒷주머니" }, smv: 1.4, operators: 3, performance: 0.82, state: "running", downtimeMin: 0 },
        { id: "L5-02", op: { en: "Front pocket", ko: "앞주머니" }, smv: 1.5, operators: 2, performance: 0.84, state: "running", downtimeMin: 0 },
        { id: "L5-03", op: { en: "Fly zip", ko: "지퍼 앞단" }, smv: 1.3, operators: 2, performance: 0.8, state: "running", downtimeMin: 12 },
        { id: "L5-04", op: { en: "Inseam", ko: "안솔기" }, smv: 0.9, operators: 1, performance: 0.86, state: "running", downtimeMin: 0 },
        { id: "L5-05", op: { en: "Outseam", ko: "바깥솔기" }, smv: 1.0, operators: 2, performance: 0.85, state: "running", downtimeMin: 0 },
        { id: "L5-06", op: { en: "Waistband", ko: "허리밴드" }, smv: 2.1, operators: 2, performance: 0.81, state: "running", downtimeMin: 5 },
        { id: "L5-07", op: { en: "Belt loops", ko: "벨트 고리" }, smv: 0.8, operators: 1, performance: 0.87, state: "stopped", downtimeMin: 15 },
        { id: "L5-08", op: { en: "Bottom hem", ko: "밑단" }, smv: 0.7, operators: 1, performance: 0.88, state: "running", downtimeMin: 0 },
        { id: "L5-09", op: { en: "Bartack", ko: "바택" }, smv: 0.5, operators: 1, performance: 0.9, state: "running", downtimeMin: 0 },
      ],
    },
  ],

  // Balancing scenarios. `moves` = how many single-operator moves the
  // calculator may make. Each move takes one operator from the station
  // whose loss hurts least and gives them to the current bottleneck
  // (see balanceLine() in calc.js). It assumes operators are cross-trained.
  scenarios: [
    { id: "current", moves: 0, label: { en: "Current allocation", ko: "현재 배치" } },
    { id: "move1", moves: 1, label: { en: "Move 1 operator", ko: "작업자 1명 이동" } },
    { id: "move2", moves: 2, label: { en: "Move 2 operators", ko: "작업자 2명 이동" } },
  ],
};

/** Station is shown as "idle" (waiting for work) below this utilisation. */
export const IDLE_BELOW = 0.5;
